/**
 * The stock structure: company → point of sale → depot.
 *
 * Formerly the sites screen, and still at its address and behind its
 * permission: a depot is still the thing being managed, and the structure is
 * where it sits. Three levels, each with its own tab, plus the tree that adds
 * the stock up:
 *
 * - **Dépôts** — where each depot sits. A depot belongs to exactly one
 *   company and one place; the two selects save on change, because that is
 *   the correction an import leaves behind.
 * - **Points de vente** — the places. One place can host the depots of
 *   several companies of the group.
 * - **Sociétés** — the group's own companies. Not customers.
 * - **Vue stock** — the tree with its totals, by company or by place. Shown
 *   only with `referential.stock.read`, because it carries the reserved
 *   quantity.
 *
 * "Importer un fichier" loads the list the business keeps by hand — a title,
 * a header, a line per depot with the place that hosts it — and reports the
 * depots it could not attach to a company, so somebody does.
 *
 * Taking anything out of service rather than deleting it is the rule at every
 * level: stock, scopes and orders point at these by code.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Alert, Button, Page, Tabs } from "@hardauto/core-ui";

import { ReferentialApi, type StructureImportReport } from "../api";
import { NAMESPACE } from "../i18n";
import { CompaniesTab } from "./structure/CompaniesTab";
import { DepotsTab } from "./structure/DepotsTab";
import { ImportReport } from "./structure/ImportReport";
import { PointsOfSaleTab } from "./structure/PointsOfSaleTab";
import { StructureTreeView } from "./structure/StructureTreeView";
import "./referential.css";

export type StructureTab = "companies" | "points" | "depots" | "tree";
type TabId = StructureTab;

export function SitesScreen({
  client,
  initialTab = "companies",
  onStructureChanged,
}: {
  client: ApiClient;
  /** The tab the screen opens on — each menu entry names its own. */
  initialTab?: StructureTab;
  /**
   * Called after anything here changed the structure — a level created,
   * renamed, hidden, shown, attached, deleted or imported — so whatever the
   * application built from it elsewhere (the context, its filters) reads it
   * again instead of offering a company that was just taken out of service.
   */
  onStructureChanged?: (() => void) | undefined;
}) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const action = useAction();
  const mayWrite = useCan("referential.site.write");
  const mayReadStock = useCan("referential.stock.read");
  const mayEditPerimeter = useCan("referential.article.write");

  const [tab, setTab] = useState<TabId>(initialTab);
  const [report, setReport] = useState<StructureImportReport | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // Bumped after an import so the depots tab reopens narrowed and the tree
  // is read again — both by remounting, which is simpler than threading a
  // reload through every child.
  const [generation, setGeneration] = useState(0);
  const [fixing, setFixing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const companiesFetcher = useCallback(() => api.listCompanies(), [api]);
  const pointsFetcher = useCallback(() => api.listPointsOfSale(), [api]);
  const sitesFetcher = useCallback(() => api.listSites(), [api]);
  const companies = useResource(companiesFetcher);
  const points = useResource(pointsFetcher);
  const sites = useResource(sitesFetcher);

  // A deletion cascades — a company takes its depots — so every list it
  // can touch is read again, and the tree with them.
  const reloadAll = () => {
    companies.reload();
    points.reload();
    sites.reload();
    setGeneration((value) => value + 1);
    onStructureChanged?.();
  };

  const reloadSites = () => {
    sites.reload();
    onStructureChanged?.();
  };

  function onPick(file: File | undefined) {
    if (!file) return;
    void action.run("import", async () => {
      try {
        const outcome = await api.importStructure(file);
        setReport(outcome);
        setImportError(null);
        companies.reload();
        points.reload();
        sites.reload();
        setGeneration((value) => value + 1);
        onStructureChanged?.();
      } catch (error) {
        setReport(null);
        // The server names the columns it expected, which is the one thing
        // that tells the person what to change in their file.
        setImportError(error instanceof Error ? error.message : "");
      } finally {
        // So picking the same file again, once corrected, fires a change.
        if (fileInput.current) fileInput.current.value = "";
      }
    });
  }

  const tabs: { id: TabId; label: string; count?: number }[] = [
    // Top-down, as the structure is read: a company, the places, the depots.
    { id: "companies", label: t("structure.tabs.companies"), ...countOf(companies.data?.items) },
    { id: "points", label: t("structure.tabs.points"), ...countOf(points.data?.items) },
    { id: "depots", label: t("structure.tabs.depots"), ...countOf(sites.data?.items) },
    ...(mayReadStock ? [{ id: "tree" as const, label: t("structure.tabs.tree") }] : []),
  ];

  return (
    <Page
      title={t("structure.title")}
      description={t("structure.intro")}
      actions={
        mayWrite ? (
          <>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept=".xlsx,.csv"
              aria-label={t("structure.import.fileLabel")}
              onChange={(event) => onPick(event.target.files?.[0])}
            />
            <Button
              variant="primary"
              busy={action.isBusy("import")}
              busyLabel={t("structure.import.busy")}
              onClick={() => fileInput.current?.click()}
            >
              {t("structure.import.button")}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="hardauto-structure">
        {importError !== null ? (
          <Alert tone="danger" title={t("structure.import.failed")}>
            {importError || t("error.title")}
          </Alert>
        ) : null}
        {report ? (
          <ImportReport
            report={report}
            onDismiss={() => setReport(null)}
            onFix={() => {
              setFixing(true);
              setTab("depots");
              setGeneration((value) => value + 1);
            }}
          />
        ) : null}

        <Tabs
          label={t("structure.tabs.label")}
          active={tab}
          onSelect={(id) => setTab(id as TabId)}
          items={tabs}
        />

        {tab !== "tree" ? <p className="hardauto-cell__muted">{t("visibility.help")}</p> : null}

        {tab === "depots" ? (
          <DepotsTab
            key={generation}
            api={api}
            sites={sites.data?.items ?? []}
            companies={companies.data?.items ?? []}
            points={points.data?.items ?? []}
            loading={sites.loading}
            failed={sites.failed}
            reload={reloadSites}
            mayWrite={mayWrite}
            initiallyUnattachedOnly={fixing}
          />
        ) : null}
        {tab === "points" ? (
          <PointsOfSaleTab
            api={api}
            points={points.data?.items ?? []}
            sites={sites.data?.items ?? []}
            loading={points.loading}
            failed={points.failed}
            reload={reloadAll}
            mayWrite={mayWrite}
          />
        ) : null}
        {tab === "companies" ? (
          <CompaniesTab
            api={api}
            companies={companies.data?.items ?? []}
            sites={sites.data?.items ?? []}
            loading={companies.loading}
            failed={companies.failed}
            reload={reloadAll}
            mayWrite={mayWrite}
            mayEditPerimeter={mayEditPerimeter}
          />
        ) : null}
        {tab === "tree" && mayReadStock ? <StructureTreeView key={generation} api={api} /> : null}
      </div>
    </Page>
  );
}

/** A tab's count, absent until the list has loaded. */
function countOf(items: readonly unknown[] | undefined): { count?: number } {
  return items ? { count: items.length } : {};
}
